import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";

type ScrollRevealVariant = "section" | "visual" | "item";

export function ScrollReveal({
  as: Component = "div",
  children,
  className = "",
  delay = 0,
  id,
  variant = "section",
}: {
  as?: "div" | "section" | "article";
  children: ReactNode;
  className?: string;
  delay?: number;
  id?: string;
  variant?: ScrollRevealVariant;
}) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      node.classList.add("is-visible");
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          node.classList.add("is-visible");
          observer.unobserve(node);
        }
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.18 },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return (
    <Component
      id={id}
      ref={ref as never}
      className={[
        "scroll-reveal",
        `scroll-reveal-${variant}`,
        className,
      ].join(" ")}
      style={{ "--reveal-delay": `${delay}ms` } as CSSProperties}
    >
      {children}
    </Component>
  );
}
